import { useCallback, useRef, useState } from "react";
import { readExamDraft, writeExamDraft, removeExamDraft } from "../utils/examDraft";

// Resolve localStorage inside the protected helpers: even accessing the browser
// property can throw when storage is disabled. Public modes never call these.
const browserStorage = {
  getItem: key => window.localStorage.getItem(key),
  setItem: (key, value) => window.localStorage.setItem(key, value),
  removeItem: key => window.localStorage.removeItem(key)
};

export default function useExamDraft({ identity, enabled, initialAnswers, readOnly }) {
  const [loaded] = useState(() => enabled
    ? readExamDraft(identity, browserStorage)
    : { answers: null, error: null });
  const [answers, setAnswers] = useState(() => loaded.answers ?? initialAnswers);
  const latest = useRef(answers);
  const finished = useRef(false);
  const [saveWarning, setSaveWarning] = useState(
    loaded.error || (enabled && !identity
      ? "Local recovery is unavailable for this exam session. Please tell your proctor before continuing."
      : null)
  );

  const updateAnswer = useCallback((id, value) => {
    if (readOnly || finished.current) return;
    const next = { ...latest.current, [id]: value };
    latest.current = next;
    setAnswers(next);
    // Synchronous per action, outside a React state updater/effect. Even an
    // immediate refresh or several edits in one React batch keeps the latest work.
    if (enabled && identity) {
      const saved = writeExamDraft(identity, next, browserStorage);
      setSaveWarning(saved ? null :
        "This browser could not save your latest work. Please tell your proctor and avoid refreshing or closing this page.");
    }
  }, [enabled, identity, readOnly]);

  const clearDraft = useCallback(() => {
    finished.current = true;
    if (enabled) removeExamDraft(identity, browserStorage);
  }, [enabled, identity]);

  return { answers, updateAnswer, clearDraft, saveWarning, restored: loaded.answers !== null };
}
